import { Box } from "@mui/material";
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";

import ActiveImplementationsTab from "./activeImplementationsTab";
import ApiErrorModal from "./components/ApiErrorModal";
import LoadingOverlay from "./components/LoadingOverlay";
import { Sidebar } from "./components/Sidebar";
import CARR from "./components/carr";
import Deals from "./components/deals";
import DetailsGrid from "./components/detailsgrid";
import Header from "./components/header";
import Implementations from "./components/implementations";
import Invoices from "./components/invoices";
import Licenses from "./components/licenses";
import SearchBar from "./components/searchbar";
import Tickets from "./components/tickets";
// import RenewalTab from "./renewalTab";
import CustomersTab from "./customersTab";
import { checkCustomerParam, fetchData, fetchServerConfig } from "./dataHandler";
import DealSummaryTab from "./dealSummaryTab";
import "./index.css";
import MetricsTab from "./metricsTab";
import ProductHealthTab from "./productHealthTab";
import ProductTab from "./productTab";
import reportWebVitals from "./reportWebVitals";
import ReportsTab from "./reportsTab";
import SupportTab from "./supportTab";

const root = ReactDOM.createRoot(document.getElementById("root"));

const deployment_date = "3-26-2025 | 10:50am EST";

// Tab name to index mapping
const TAB_NAME_TO_INDEX = {
    search: 0,
    profiles: 0,
    customers: 1,
    allcustomers: 1,
    dealsummary: 2,
    deals: 2,
    implementations: 3,
    support: 4,
    metrics: 5,
    reports: 6,
    producthealth: 7,
    health: 7,
    products: 8,
    productteams: 8,
};

// Index to preferred tab name (for URL display)
const TAB_INDEX_TO_NAME = {
    0: "search",
    1: "customers",
    2: "dealsummary",
    3: "implementations",
    4: "support",
    5: "metrics",
    6: "reports",
    7: "producthealth",
    8: "productteams",
};

const getTabFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (!tabParam) return 0;

    const normalizedName = tabParam.toLowerCase().replace(/[^a-z]/g, "");
    if (TAB_NAME_TO_INDEX.hasOwnProperty(normalizedName)) {
        return TAB_NAME_TO_INDEX[normalizedName];
    }

    return 0;
};

const updateUrlTab = (tabIndex) => {
    const url = new URL(window.location);
    const tabName = TAB_INDEX_TO_NAME[tabIndex] || tabIndex;
    url.searchParams.set("tab", tabName);
    // Remove id and dealid when navigating via sidebar
    url.searchParams.delete("id");
    url.searchParams.delete("dealid");
    window.history.replaceState({}, "", url);
};

const App = () => {
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState();
    const [errorText, setErrorText] = useState();
    const [showHeaderSearch, setShowHeaderSearch] = useState(false);
    const [selectedTab, setSelectedTab] = useState(() => getTabFromUrl());
    const [accessCheckComplete, setAccessCheckComplete] = useState(false);
    const [queriesRun, setQueriesRun] = useState(false);

    const [custDataFetched, setCustDataFetched] = useState(false);
    const [prodDataFetched, setProdDataFetched] = useState(false);
    const [metricsDataFetched, setMetricsDataFetched] = useState(false);
    const [activeImplementationsDataFetched, setActiveImplementationsDataFetched] = useState(false);
    const [supportDataFetched, setSupportDataFetched] = useState(false);

    const [customerList, setCustomerList] = useState();
    const [activeImplementationsList, setActiveImplementationsList] = useState();
    const [supportData, setSupportData] = useState();
    const [customerData, setCustomerData] = useState();
    const [productList, setProductList] = useState();
    const [metricData, setMetricData] = useState();
    // const [renewalList, setRenewalList] = useState();

    const [metricsAccess, setMetricsAccess] = useState(false);
    const [activeImplementationsAccess, setActiveImplementationsAccess] = useState(false);
    const [customerListAccess, setCustomerListAccess] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [userLoading, setUserLoading] = useState(true);
    const [dealSummaryResetKey, setDealSummaryResetKey] = useState(0);
    const [isDevRestlet, setIsDevRestlet] = useState(false);

    function classNames(...classes) {
        return classes.filter(Boolean).join(" ");
    }

    async function getCustomer(id) {
        setLoading(true);
        setCustomerData();

        fetchData("getcustomerdata", { id: id })
            .then(function (data) {
                setCustomerData(data.values);
                setShowHeaderSearch(true);
                setLoading(false);
            })
            .catch(function (error) {
                console.error("Error fetching data:", error);
                setErrorText(["Error fetching data...", <br />, "Please try again!"]);
                setSelected();
                setShowHeaderSearch(false);
                setCustomerData();
                setLoading(false);

                setTimeout(() => {
                    setErrorText();
                }, 3000);
            });
    }

    // FETCH USER INFO ON PAGE LOAD
    useEffect(() => {
        const fetchUser = async () => {
            // Bypass auth on localhost
            if (
                window.location.hostname === "localhost" ||
                window.location.hostname === "127.0.0.1"
            ) {
                setUser({ name: "Dev User", email: "dev@netgain.tech" });
                setUserLoading(false);
                return;
            }

            try {
                const response = await fetch("/api/auth/me");
                const data = await response.json();
                setUser(data);
            } catch (error) {
                console.error("Error fetching user:", error);
                setUser(null);
            } finally {
                setUserLoading(false);
            }
        };
        fetchUser();
    }, []);

    // Scroll to top when customer is selected
    useEffect(() => {
        if (selected) {
            // Multiple scroll attempts to counter auto-focus scroll
            window.scrollTo(0, 0);
            setTimeout(() => window.scrollTo(0, 0), 100);
            setTimeout(() => window.scrollTo(0, 0), 300);
            setTimeout(() => window.scrollTo(0, 0), 500);
        }
    }, [selected]);

    const handleLogout = async () => {
        try {
            const response = await fetch("/api/auth/logout", { method: "POST" });
            const data = await response.json();
            if (data.logoutUrl) {
                window.location.href = data.logoutUrl;
            } else {
                setUser(null);
                window.location.reload();
            }
        } catch (error) {
            console.error("Error logging out:", error);
        }
    };

    // RUN ACCESS CHECKS ON PAGE LOAD
    useEffect(() => {
        const runAccessCheck = async () => {
            // Bypass API call on localhost - grant all permissions
            // if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            //     setMetricsAccess(true);
            //     setCustomerListAccess(true);
            //     setActiveImplementationsAccess(true);
            //     setAccessCheckComplete(true);
            //     return;
            // }

            await fetchData("access")
                .then(function (data) {
                    let hasCustomerListAccess = false;
                    for (let row of data) {
                        if (row === "VIEW_METRICS_PAGE") setMetricsAccess(true);
                        if (row === "VIEW_FULL_CUSTOMER_LIST") {
                            setCustomerListAccess(true);
                            hasCustomerListAccess = true;
                        }
                        if (row === "VIEW_ACTIVE_IMPLEMENTATIONS_PAGE")
                            setActiveImplementationsAccess(true);
                    }
                    // If user has customer list access and no tab specified in URL, default to All Customers
                    const params = new URLSearchParams(window.location.search);
                    if (hasCustomerListAccess && !params.get("tab")) {
                        setSelectedTab(1);
                    }
                })
                .catch(function (error) {
                    console.error("Error fetching data:", error);
                });

            setAccessCheckComplete(true);
        };

        runAccessCheck();
    }, []);

    // FETCH DATA ONCE ACCESS CHECKS ARE COMPLETE
    useEffect(() => {
        setLoading(true);

        const runQueries = async () => {
            // FULL CUSTOMER LIST - for search use and full list page
            if (!customerList && !custDataFetched) {
                fetchData("listcustomers")
                    .then(function (data) {
                        setCustomerList(data.values);
                        setCustDataFetched(true);
                    })
                    .catch(function (error) {
                        console.error("Error fetching customer list:", error);
                        setCustDataFetched(false);
                    });
            }

            // PRODUCT LIST - for product teams page
            if (!productList && !prodDataFetched) {
                fetchData("listproducts")
                    .then(function (data) {
                        setProductList(data.values);
                        setProdDataFetched(true);
                    })
                    .catch(function (error) {
                        console.error("Error fetching product team:", error);
                        setProdDataFetched(false);
                    });

                // Fetch server config (dev restlet indicator)
                fetchServerConfig()
                    .then((config) => setIsDevRestlet(config.isDevRestlet))
                    .catch((error) => console.error("Error fetching server config:", error));
            }

            // ACTIVE IMPLEMENTATIONS LIST - for active implementations page
            if (
                activeImplementationsAccess &&
                !activeImplementationsList &&
                !activeImplementationsDataFetched
            ) {
                fetchData("listactiveimplementations")
                    .then(function (data) {
                        setActiveImplementationsList(data.values);
                        setActiveImplementationsDataFetched(true);
                    })
                    .catch(function (error) {
                        console.error("Error fetching active implementations:", error);
                        setActiveImplementationsDataFetched(false);
                    });
            }

            // SUPPORT TICKETS - for support tab - requires same access as implementations
            if (activeImplementationsAccess && !supportData && !supportDataFetched) {
                fetchData("listsupporttickets")
                    .then(function (data) {
                        setSupportData(data.values);
                        setSupportDataFetched(true);
                    })
                    .catch(function (error) {
                        console.error("Error fetching support tickets:", error);
                        setSupportDataFetched(false);
                    });
            }

            // METRICS - for metrics page - requires access
            if (metricsAccess && !metricData && !metricsDataFetched) {
                const todayDate = new Date().toISOString().split("T")[0];
                fetchData("getmetrics", { date: todayDate })
                    .then((data) => {
                        setMetricData(data.values);
                        setMetricsDataFetched(true);
                    })
                    .catch((error) => {
                        console.error("Error fetching metrics:", error);
                        setMetricsDataFetched(false);
                    });
            }

            // bypassing for now
            // fetchData("listrenewals")
            //   .then(function (data) {
            //     setRenewalList(data.values);
            //   })
            //   .catch(function (error) {
            //     console.error("Error fetching data:", error);
            //   });
        };

        if (accessCheckComplete && !queriesRun) {
            runQueries();
            setQueriesRun(true);
        }

        // only run after all the others have successfully fetched data and there is an selected item
        if (customerList && productList && selected && selected.id) getCustomer(selected.id);

        // CUSTOMER LIST AND PRODUCT LIST FETCHED - STOP LOADING
        if (custDataFetched && prodDataFetched) setLoading(false);
    }, [
        metricsAccess,
        metricData,
        accessCheckComplete,
        customerList,
        productList,
        activeImplementationsList,
        selected,
        custDataFetched,
        prodDataFetched,
        queriesRun,
        metricsDataFetched,
        activeImplementationsDataFetched,
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [
    //   accessCheckComplete,
    //   metricsAccess,
    //   custDataFetched,
    //   prodDataFetched,
    //   selected,
    // ]);

    useEffect(() => {
        if (!selected) {
            setShowHeaderSearch(false);
            checkCustomerParam().then((data) => {
                if (data) setSelected({ id: data });
            });
        }
    }, [selected]);

    // Hide vertical scrollbar on Product tab
    useEffect(() => {
        document.body.style.overflowY = selectedTab === 8 ? "hidden" : "auto";
        return () => {
            document.body.style.overflowY = "auto";
        };
    }, [selectedTab]);

    // Show redirect message when not authenticated
    if (userLoading) {
        return <LoadingOverlay />;
    }

    if (!user) {
        // Redirect to Microsoft login with return URL
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/api/auth/sso/login?returnUrl=${returnUrl}`;
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    backgroundColor: "#f4f6f9",
                    fontFamily: "Poppins, sans-serif",
                }}
            >
                <p>Redirecting to Microsoft login...</p>
            </div>
        );
    }

    return (
        <React.StrictMode>
            {/* Global popup for API failures on any page that uses the data handler */}
            <ApiErrorModal />
            <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f4f6f9" }}>
                {/* Loading overlay while access check is in progress */}
                {!accessCheckComplete && <LoadingOverlay fixed />}
                <Sidebar
                    sx={{ visibility: accessCheckComplete ? "visible" : "hidden" }}
                    customerListAccess={customerListAccess}
                    activeImplementationsAccess={activeImplementationsAccess}
                    metricsAccess={metricsAccess}
                    accessCheckComplete={accessCheckComplete}
                    selectedTab={selectedTab}
                    isOpen={sidebarOpen}
                    onToggle={() => setSidebarOpen(!sidebarOpen)}
                    user={user}
                    userLoading={userLoading}
                    onLogout={handleLogout}
                    onTabChange={(index) => {
                        if (index === 0) {
                            setShowHeaderSearch(!!selected);
                            setSelected(undefined);
                            setCustomerData(undefined);
                        } else {
                            setShowHeaderSearch(false);
                        }

                        // Reset deal summary view when clicking Deal Summary tab
                        if (index === 2) {
                            setDealSummaryResetKey((prev) => prev + 1);
                        }

                        setSelectedTab(index);
                        updateUrlTab(index);
                        window.scrollTo(0, 0);
                    }}
                />

                <Box
                    id="main-content"
                    sx={{
                        marginLeft: sidebarOpen ? "200px" : "60px",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        transition: "margin-left 0.3s ease-in-out",
                        visibility: accessCheckComplete ? "visible" : "hidden",
                    }}
                >
                    {/* Page content */}
                    <Box sx={{ p: 2 }}>
                        <Header
                            loading={loading}
                            setLoading={setLoading}
                            selected={selected}
                            setSelected={setSelected}
                            customerList={customerList}
                            showSearch={showHeaderSearch}
                        />

                        <div className="flex h-full w-full flex-col pb-16 pt-0">
                            <div
                                key={selectedTab}
                                className="tab-panel ngShadow tab-content-animate w-full"
                                style={selectedTab === 7 ? { maxWidth: 1600 } : undefined}
                            >
                                {/* TAB 1 - Search / Profile */}
                                {selectedTab === 0 && (
                                    <>
                                        {accessCheckComplete && !custDataFetched && loading ? (
                                            <LoadingOverlay text="Loading customer data..." />
                                        ) : errorText ? (
                                            <h3 className="px-2 pt-6 text-center">{errorText}</h3>
                                        ) : (
                                            <>
                                                {!showHeaderSearch && !selected ? (
                                                    <div className="flex flex-col items-center">
                                                        <h1 className="px-2 pb-10 pt-6 text-center font-black xs:pb-6">
                                                            Netgain Customer Profiles
                                                        </h1>
                                                        <SearchBar
                                                            data-id="search-bar"
                                                            loading={loading}
                                                            selected={selected}
                                                            customerList={customerList}
                                                            setLoading={setLoading}
                                                            setSelected={setSelected}
                                                        />
                                                    </div>
                                                ) : null}

                                                {selected && !loading && customerData ? (
                                                    <>
                                                        <DetailsGrid customerData={customerData} />
                                                        {customerData?.deals && (
                                                            <Deals data={customerData.deals} />
                                                        )}
                                                        {customerData?.licenses && (
                                                            <Licenses
                                                                data={customerData.licenses}
                                                            />
                                                        )}
                                                        {customerData?.implementations && (
                                                            <Implementations
                                                                data={customerData.implementations}
                                                            />
                                                        )}
                                                        {customerData?.tickets && (
                                                            <Tickets data={customerData.tickets} />
                                                        )}
                                                        {customerData?.carr_by_product && (
                                                            <CARR
                                                                data={customerData.carr_by_product}
                                                            />
                                                        )}
                                                        {customerData?.invoices_and_payments && (
                                                            <Invoices
                                                                data={
                                                                    customerData.invoices_and_payments
                                                                }
                                                            />
                                                        )}
                                                    </>
                                                ) : (
                                                    selected &&
                                                    !customerData &&
                                                    customerList && (
                                                        <LoadingOverlay
                                                            text={`Loading customer #${selected.id}...`}
                                                        />
                                                    )
                                                )}
                                            </>
                                        )}
                                    </>
                                )}

                                {/* TAB 2 - All Customers List - Conditional */}
                                {selectedTab === 1 && customerListAccess && (
                                    <CustomersTab
                                        data={customerList}
                                        setSelectedTab={setSelectedTab}
                                        setSelected={setSelected}
                                    />
                                )}

                                {/* TAB 3 - Deal Summary - Conditional */}
                                {selectedTab === 2 && (
                                    <DealSummaryTab
                                        fetchData={fetchData}
                                        resetKey={dealSummaryResetKey}
                                        customerList={customerList}
                                    />
                                )}

                                {/* TAB 4 - Active Implementations - Conditional */}
                                {selectedTab === 3 && activeImplementationsAccess && (
                                    <ActiveImplementationsTab
                                        data={activeImplementationsList}
                                        setActiveImplementationsList={setActiveImplementationsList}
                                        setSelectedTab={setSelectedTab}
                                        setSelected={setSelected}
                                    />
                                )}

                                {/* TAB 5 - Support - Conditional */}
                                {selectedTab === 4 && activeImplementationsAccess && (
                                    <SupportTab data={supportData} />
                                )}

                                {/* TAB 6 - Metrics - Conditional */}
                                {selectedTab === 5 && metricsAccess && (
                                    <MetricsTab
                                        data={metricData}
                                        fetchData={fetchData}
                                        loading={loading}
                                    />
                                )}

                                {/* TAB 7 - Reports - Conditional */}
                                {selectedTab === 6 && metricsAccess && (
                                    <ReportsTab
                                        data={metricData}
                                        fetchData={fetchData}
                                        loading={loading}
                                    />
                                )}

                                {/* TAB 8 - Product Health - Conditional */}
                                {selectedTab === 7 && metricsAccess && (
                                    <ProductHealthTab fetchData={fetchData} />
                                )}

                                {/* TAB 9 - Product Teams */}
                                {selectedTab === 8 && <ProductTab data={productList} />}
                            </div>

                            {isDevRestlet && (
                                <div className="fixed bottom-4 right-4 rounded bg-orange-100 px-3 py-1 text-xs text-orange-700 shadow">
                                    ⚠ Using dev RESTlet
                                </div>
                            )}

                            {/* <div className="relative bottom-0 mt-10">
                    <div className="flex flex-col items-center">
                        <span style={{ color: 'rgba(0,0,0,0.5)' }} className="pb-1">
                            Developed by:{' '}
                            <a href="mailto:habbott@netgain.tech" target="_blank" rel="noreferrer">
                                Henry Abbott
                            </a>{' '}
                            &{' '}
                            <a href="mailto:astafford@netgain.tech" target="_blank" rel="noreferrer">
                                Andrew Stafford
                            </a>
                        </span>
                        <span style={{ color: 'rgba(0,0,0,0.5)' }} className="text-sm">
                            Last Portal Deployment: {deployment_date}
                        </span>
                    </div>
                </div> */}
                        </div>
                    </Box>
                </Box>
            </Box>
        </React.StrictMode>
    );
};
root.render(<App style={{ height: "100vh" }} />);
reportWebVitals();
